import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  feeCompanySubscriptionCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { feeEngineEndpoints } from "@/features/fee-engine/api/endpoints";
import type { CompanySubscriptionDto } from "@/features/fee-engine/api/dto";

/**
 * Annule un abonnement entreprise (`POST /CompanySubscriptions/{id}/cancel`,
 * guide §6.5). Rôles `flashpay-superadmin, flashpay-admin`.
 */
export const useCancelCompanySubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["fee-engine", "company-subscriptions", "cancel"],
    mutationFn: (companySubscriptionId: string) =>
      mutateData({
        url: feeEngineEndpoints.companySubscriptions.cancel(
          companySubscriptionId
        ),
        method: "POST",
      }) as Promise<CompanySubscriptionDto>,
    onSuccess: (_data, companySubscriptionId) => {
      return invalidateAfterMutation(
        queryClient,
        feeCompanySubscriptionCacheKeys(companySubscriptionId)
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useCancelCompanySubscription failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "fee-engine" }
      );
    },
  });
};
